import apiClient from "./apiClient"
import { AuthResponse, AuthUser, CashierFlow } from "../models/auth"

const CASHIER_FLOW_STORAGE_KEY = "cashierFlow"

export function getCachedCashierFlow(): CashierFlow | null {
    const raw = localStorage.getItem(CASHIER_FLOW_STORAGE_KEY)
    if (!raw) return null

    try {
        return JSON.parse(raw) as CashierFlow
    } catch (_error) {
        localStorage.removeItem(CASHIER_FLOW_STORAGE_KEY)
        return null
    }
}

function cacheCashierFlow(flow: CashierFlow | null): void {
    if (!flow) {
        localStorage.removeItem(CASHIER_FLOW_STORAGE_KEY)
        return
    }

    localStorage.setItem(CASHIER_FLOW_STORAGE_KEY, JSON.stringify(flow))
}

function persistTokens(response: AuthResponse): void {
    if (!response.accessToken) return
    apiClient.setToken(response.accessToken)
    if (response.refreshToken) {
        localStorage.setItem("refreshToken", response.refreshToken)
    }
    // shared token for cross-app SSO (dev: localStorage, prod: domain cookie set server-side)
    localStorage.setItem("cileamoov_access_token", response.accessToken)
}

export async function register(
    userData: Record<string, unknown>
): Promise<AuthResponse> {
    const response = (await apiClient.post(
        "/api/auth/register",
        userData
    )) as AuthResponse
    persistTokens(response)
    cacheCashierFlow(response.cashierFlow || null)
    return response
}

export async function login(
    email: string,
    password: string
): Promise<AuthResponse> {
    const response = (await apiClient.post("/api/auth/login", {
        email,
        password,
    })) as AuthResponse
    persistTokens(response)
    cacheCashierFlow(response.cashierFlow || null)
    return response
}

export async function logout(): Promise<void> {
    try {
        await apiClient.post("/api/auth/logout")
    } catch (error) {
        console.error("Logout error:", error)
    } finally {
        apiClient.removeToken()
        localStorage.removeItem("refreshToken")
        localStorage.removeItem(CASHIER_FLOW_STORAGE_KEY)
    }
}

export async function refreshAccessToken(): Promise<AuthResponse> {
    const refreshToken = localStorage.getItem("refreshToken")
    if (!refreshToken) {
        throw new Error("No refresh token available")
    }

    const response = (await apiClient.post("/api/auth/refresh", {
        refreshToken,
    })) as AuthResponse
    persistTokens(response)
    return response
}

export async function partnerLogin(
    partnerToken: string
): Promise<AuthResponse> {
    const response = (await apiClient.post("/api/auth/partner-login", {
        partnerToken,
    })) as AuthResponse
    persistTokens(response)
    cacheCashierFlow(response.cashierFlow || null)
    return response
}

export async function getCurrentUser(): Promise<AuthUser> {
    const response = (await apiClient.get("/api/auth/me")) as { user: AuthUser }
    return response.user
}

export function isAuthenticated(): boolean {
    return !!localStorage.getItem("accessToken")
}

const authService = {
    register,
    login,
    logout,
    refreshAccessToken,
    getCurrentUser,
    isAuthenticated,
    partnerLogin,
}

export default authService
